import React, { useState, useEffect, useRef } from 'react';
import { Image, Upload, FolderOpen, X, Check, Loader2, Link2, ChevronRight, Home, Folder } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import mediaApi from '@/api/media';

/**
 * ImagePicker - A reusable component for selecting/uploading images
 * 
 * Features:
 * - Upload new images directly
 * - Select from Media Library with folder browsing
 * - Paste image URL
 * - Preview current image
 * 
 * @param {string} value - Current image URL
 * @param {function} onChange - Callback when image changes (receives URL string)
 * @param {string} label - Optional label for the field
 * @param {string} placeholder - Placeholder text for URL input
 * @param {string} aspectRatio - CSS aspect ratio (e.g., "16/9", "1/1", "auto")
 * @param {string} className - Additional CSS classes
 * @param {boolean} showPreview - Show image preview (default: true)
 * @param {string} folder - Media library folder for uploads
 * @param {string} testId - data-testid attribute
 */
export function ImagePicker({
  value,
  onChange,
  label,
  placeholder = "Enter image URL or select from library",
  aspectRatio = "16/9",
  className,
  showPreview = true,
  folder = null,
  testId = "image-picker",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('library');
  const [mediaItems, setMediaItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]); // Breadcrumb path
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef(null);

  // Load media library when dialog opens
  useEffect(() => {
    if (isOpen) {
      loadFolderContents(null);
      setUrlInput(value || '');
      setSelectedImage(null);
      setFolderPath([]);
      setCurrentFolder(null);
    }
  }, [isOpen]);

  const loadFolderContents = async (folderId) => {
    setLoading(true);
    try {
      // Load subfolders
      const foldersResult = await mediaApi.listFolders(folderId);
      if (foldersResult.success) {
        setFolders(foldersResult.data || []);
      }
      
      // Load images in this folder
      const params = { media_type: 'image', limit: 100 };
      if (folderId) params.folder = folderId;
      const imagesResult = await mediaApi.list(params);
      if (imagesResult.success) {
        setMediaItems(imagesResult.data || []);
      }
    } catch (error) {
      console.error('Failed to load media library:', error);
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = (folder) => {
    if (folder === null) {
      // Going to root
      setCurrentFolder(null);
      setFolderPath([]);
      loadFolderContents(null);
    } else {
      setCurrentFolder(folder);
      setFolderPath([...folderPath, folder]);
      loadFolderContents(folder.id);
    }
  };

  const navigateToBreadcrumb = (index) => {
    if (index === -1) {
      // Root
      setCurrentFolder(null);
      setFolderPath([]);
      loadFolderContents(null);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      const targetFolder = newPath[index];
      setCurrentFolder(targetFolder);
      setFolderPath(newPath);
      loadFolderContents(targetFolder.id);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      // Determine upload options
      // If we're in a folder (currentFolder), use its ID
      // If a folder prop was passed (like "pages/abc123"), use it as a path
      const uploadOptions = {};
      if (currentFolder?.id) {
        uploadOptions.folder = currentFolder.id;
      } else if (folder) {
        // Check if folder looks like a path (contains /) or an ID (UUID-like)
        if (folder.includes('/') || !folder.match(/^[a-f0-9-]{36}$/i)) {
          uploadOptions.folder_path = folder;
        } else {
          uploadOptions.folder = folder;
        }
      }
      
      const result = await mediaApi.upload(file, uploadOptions);
      if (result.success) {
        toast.success('Image uploaded successfully!');
        // Refresh library and select the new image
        await loadFolderContents(currentFolder?.id || null);
        const imageUrl = result.data.url;
        const fallbackUrl = result.data.fallback_url;
        setSelectedImage({ url: imageUrl, fallback_url: fallbackUrl });
        setActiveTab('library');
      } else {
        toast.error(result.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleConfirm = () => {
    if (activeTab === 'url' && urlInput) {
      onChange(urlInput, null);
    } else if (selectedImage) {
      // selectedImage can be an object with url and fallback_url, or just a url string
      if (typeof selectedImage === 'object') {
        onChange(selectedImage.url, selectedImage.fallback_url);
      } else {
        onChange(selectedImage, null);
      }
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setSelectedImage(null);
    setUrlInput('');
  };

  const getImageUrl = (item) => {
    // Handle both URL strings and media objects
    if (typeof item === 'string') return item;
    return item.url || mediaApi.getFileUrl(item.file_path);
  };

  // Get folder display name (clean up GUID folders and show friendly names)
  const getFolderDisplayName = (folder) => {
    const name = folder.name || '';
    
    // Known folder types with friendly names
    const friendlyNames = {
      'pages': '📄 Landing Pages',
      'qr_codes': '📱 QR Codes', 
      'documents': '📁 Documents',
      'logos': '🏢 Logos',
      'agents': '👤 Agents',
      'images': '🖼️ Images',
    };
    
    if (friendlyNames[name]) {
      return friendlyNames[name];
    }
    
    // If it's a UUID-like name, try to make it more readable
    if (/^[a-f0-9-]{36}$/i.test(name)) {
      return `📄 ${name.slice(0, 8)}...`;
    }
    
    return name;
  };

  return (
    <div className={cn("space-y-2", className)} data-testid={testId}>
      {label && <Label>{label}</Label>}
      
      {/* Current Value Display / Trigger */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="pr-20"
            data-testid={`${testId}-url-input`}
          />
          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-1">
            {value && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleClear}
                data-testid={`${testId}-clear-btn`}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setIsOpen(true)}
              data-testid={`${testId}-browse-btn`}
            >
              <FolderOpen className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Preview */}
      {showPreview && value && (
        <div 
          className="relative rounded-lg border overflow-hidden bg-muted"
          style={{ aspectRatio }}
          data-testid={`${testId}-preview`}
        >
          <img
            src={value}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}

      {/* Media Library Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              Select Image
            </DialogTitle>
            <DialogDescription>
              Upload a new image, select from your media library, or paste a URL
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="library" className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4" />
                Library
              </TabsTrigger>
              <TabsTrigger value="upload" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload
              </TabsTrigger>
              <TabsTrigger value="url" className="flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                URL
              </TabsTrigger>
            </TabsList>

            {/* Library Tab */}
            <TabsContent value="library" className="mt-4">
              {/* Breadcrumb Navigation */}
              <div className="flex items-center gap-1 mb-4 p-2 bg-muted/50 rounded-lg overflow-x-auto">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-7 px-2 shrink-0",
                    currentFolder === null && "bg-background shadow-sm"
                  )}
                  onClick={() => navigateToBreadcrumb(-1)}
                >
                  <Home className="h-4 w-4 mr-1" />
                  Root
                </Button>
                {folderPath.map((folder, index) => (
                  <React.Fragment key={folder.id}>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-7 px-2 shrink-0",
                        index === folderPath.length - 1 && "bg-background shadow-sm"
                      )}
                      onClick={() => navigateToBreadcrumb(index)}
                    >
                      {getFolderDisplayName(folder)}
                    </Button>
                  </React.Fragment>
                ))}
              </div>

              <ScrollArea className="h-[400px] pr-4">
                {loading ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-4 gap-3">
                      {[...Array(4)].map((_, i) => (
                        <Skeleton key={`folder-${i}`} className="h-12 rounded-lg" />
                      ))}
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      {[...Array(8)].map((_, i) => (
                        <Skeleton key={`img-${i}`} className="aspect-square rounded-lg" />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Folders */}
                    {folders.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">Folders</p>
                        <div className="grid grid-cols-4 gap-3">
                          {folders.map((folder) => (
                            <button
                              key={folder.id}
                              type="button"
                              onClick={() => navigateToFolder(folder)}
                              className="flex items-center gap-2 p-3 rounded-lg border hover:border-primary hover:bg-muted/50 transition-colors text-left"
                            >
                              <Folder className="h-5 w-5 text-primary shrink-0" />
                              <span className="text-sm font-medium truncate">
                                {getFolderDisplayName(folder)}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Images */}
                    {mediaItems.length > 0 ? (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">
                          Images {currentFolder && `in "${getFolderDisplayName(currentFolder)}"`}
                        </p>
                        <div className="grid grid-cols-4 gap-3">
                          {mediaItems.map((item) => {
                            const imageUrl = getImageUrl(item);
                            const isSelected = selectedImage && (
                              (typeof selectedImage === 'object' && selectedImage.url === imageUrl) ||
                              selectedImage === imageUrl
                            );
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => setSelectedImage({ 
                                  url: imageUrl, 
                                  fallback_url: item.fallback_url 
                                })}
                                className={cn(
                                  "relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:opacity-90",
                                  isSelected 
                                    ? "border-primary ring-2 ring-primary/20" 
                                    : "border-transparent hover:border-muted-foreground/30"
                                )}
                                data-testid={`media-item-${item.id}`}
                              >
                                <img
                                  src={imageUrl}
                                  alt={item.filename || 'Media'}
                                  className="w-full h-full object-cover"
                                />
                                {isSelected && (
                                  <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                    <div className="bg-primary text-primary-foreground rounded-full p-1">
                                      <Check className="h-4 w-4" />
                                    </div>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : folders.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                        <Image className="h-12 w-12 mb-4 opacity-50" />
                        <p className="text-sm">No images in this folder</p>
                        <Button
                          variant="link"
                          className="mt-2"
                          onClick={() => setActiveTab('upload')}
                        >
                          Upload your first image
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                        <Image className="h-8 w-8 mb-2 opacity-50" />
                        <p className="text-sm">No images in this folder</p>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Upload Tab */}
            <TabsContent value="upload" className="mt-4">
              <div className="flex flex-col items-center justify-center h-[400px] border-2 border-dashed rounded-lg">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid={`${testId}-file-input`}
                />
                
                {uploading ? (
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Uploading...</p>
                  </div>
                ) : (
                  <>
                    <Upload className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-sm text-muted-foreground mb-4">
                      Drag and drop an image, or click to browse
                    </p>
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid={`${testId}-upload-btn`}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Choose File
                    </Button>
                    <p className="text-xs text-muted-foreground mt-4">
                      PNG, JPG, GIF, WebP up to 5MB
                    </p>
                    {currentFolder && (
                      <p className="text-xs text-primary mt-2">
                        Will upload to: {getFolderDisplayName(currentFolder)}
                      </p>
                    )}
                  </>
                )}
              </div>
            </TabsContent>

            {/* URL Tab */}
            <TabsContent value="url" className="mt-4">
              <div className="space-y-4">
                <div>
                  <Label>Image URL</Label>
                  <Input
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    data-testid={`${testId}-url-field`}
                  />
                </div>
                
                {urlInput && (
                  <div className="rounded-lg border overflow-hidden bg-muted aspect-video">
                    <img
                      src={urlInput}
                      alt="Preview"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>';
                      }}
                    />
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedImage && !urlInput}
              data-testid={`${testId}-confirm-btn`}
            >
              <Check className="h-4 w-4 mr-2" />
              Select Image
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ImagePicker;
